/* One screen per swipe. After the fingers lift, a trackpad keeps sending wheel events for a second
   or more while the scroll coasts (Safari keeps them coming until the trackpad is touched again), so
   a new swipe is recognised by a pause in the events or by deltas that grow again, where coasting
   only ever slows down. Mouse wheels send one event per notch, with pauses between turns. */

export type WheelPagingOptions = {
  /** A gap this long (ms) between events starts a new gesture. */
  pause?: number
  /** A delta this many times larger than the two before it is a new push. */
  growth?: number
  /** Smaller deltas (px) never count as a new push: coasting jitters near the end. */
  floor?: number
  /** How far (px) a gesture travels before it moves a screen. */
  threshold?: number
}

/**
 * Feed it every wheel event: its time (ms), its delta (px) and whether a move is still under way.
 * It answers 1 or -1 when the event should move a screen down or up, otherwise 0.
 */
export const createWheelPaging = ({ pause = 200, growth = 1.5, floor = 6, threshold = 24 }: WheelPagingOptions = {}) => {
  let lastTime = Number.NEGATIVE_INFINITY
  let recent: number[] = []
  // Ready for the next move. A move disarms it; only a new gesture re-arms it, never coasting.
  let armed = true
  let total = 0

  return (time: number, delta: number, moving: boolean): -1 | 0 | 1 => {
    const magnitude = Math.abs(delta)
    const afterPause = time - lastTime > pause
    const before = afterPause ? [] : recent
    lastTime = time
    recent = [...before, magnitude].slice(-3)
    const harder = before.length >= 2 && magnitude >= floor && magnitude > Math.max(...before.slice(-2)) * growth

    if (afterPause) total = 0
    // A swipe during a move queues the next one only after a pause. Pushing harder counts once the
    // move is over, because the start of the swipe that began the move pushes harder too.
    if (afterPause || (harder && !moving)) armed = true
    if (moving || !armed) return 0
    total += delta
    if (Math.abs(total) < threshold) return 0
    const direction = total > 0 ? 1 : -1
    armed = false
    total = 0
    return direction
  }
}

/** Wheel deltas in pixels, whatever unit the browser reports them in. */
export const wheelPixels = (event: Pick<WheelEvent, 'deltaY' | 'deltaMode'>) =>
  event.deltaMode === 1 ? event.deltaY * 40 : event.deltaMode === 2 ? event.deltaY * 800 : event.deltaY
